'use client';
import { useUnreadModularObjects } from '@/apollo/reactiveVars/hooks/useUnreadModularObjects';
import { markModularObjectRead } from '@/apollo/reactiveVars/operations/mutations/unreadModularObjects';
import type {
    CreateModularObjectCardFragmentFragment,
} from '@/components/common/CreateNewObjectCard/CreateModularObject.generated';
import {
    type GetObjectCardObjectQuery,
    useGetObjectCardObjectQuery,
} from '@/components/common/ModularObject/Card/GetObjectCardObject.generated';
import { addToastError } from '@/components/Toast/utils';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useSelectedScenario } from '@/hooks/useSelectedScenario';
import { gql, useQuery } from '@apollo/client';
import {
    createContext,
    type PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type ObjectCardQueryParamValues = {
    modularObjectId?: string | 'new';
    templateId?: string;
    tid?: string; // Need to name this separately because of the templateId in the path for the object listing pages (e.g. /templates/:name/:templateId)
    isObjectCardOpen?: 'true';
    fullWidth?: 'true';
    viewCollabModal?: 'true' | 'false'; // Changed to string to satisfy Record<string, string>
};

interface OpenViewObjectCardDrawerArgs {
    modularObjectId: string;
    viewCollabModal?: string;
}

interface NewObjectConfig {
    createdFromId?: string;
    createdFromTemplateId?: string;
    asParent?: boolean;
    asDriver?: boolean;
    isDependency?: boolean;
    templateId: string;
    isBlockingDependency?: boolean;
    afterObjectCreationSave?: (modularObject: CreateModularObjectCardFragmentFragment) => void;
}

interface ObjectCardContextValue {
    isObjectCardDrawerOpen: boolean;
    isCollabModalOpen: boolean;
    isObjectCardFullWidth: boolean;
    openViewObjectCardDrawer: (objectCardDrawerArgs: OpenViewObjectCardDrawerArgs) => void;
    openCollaborationModal: () => void;
    closeCollaborationModal: () => void;
    openCreateNewObjectCardDrawer: (newObjectConfig: NewObjectConfig) => void;
    closeObjectCardDrawer: () => void;
    activeModularObjectId?: string;
    newObjectConfig?: NewObjectConfig;
    isLoadingObjectCardData: boolean;
    objectCardData?: GetObjectCardObjectQuery['getModularObjectByIDs'][0];
    handleSetExternalObjectViewer?: (
        isExternalObjectViewer: boolean,
        object: GetObjectCardObjectQuery['getModularObjectByIDs'][0],
    ) => void;
}

const ObjectCardContext = createContext<ObjectCardContextValue>(null);

export const useObjectCardContext = (): ObjectCardContextValue => {
    const context = useContext(ObjectCardContext);
    if (!context) {
        throw new Error('useObjectCardContext must be used within an ObjectCardContextProvider');
    }
    return context;
};

const safeDefault = {
    id: null,
    name: '',
    ownerId: null,
    templateId: null,
    parent: null,
    children: [],
    drivers: { edges: [] },
    approvals: [],
    diffs: [],
    integrations: [],
    approvalsEnabled: false,
} as GetObjectCardObjectQuery['getModularObjectByIDs'][0];

const getDefaultNewObjectConfig = (): NewObjectConfig => ({
    templateId: null,
    createdFromId: undefined,
    createdFromTemplateId: undefined,
    asParent: false,
    asDriver: false,
    isDependency: false,
    isBlockingDependency: false,
    afterObjectCreationSave: null,
});

export const ObjectCardContextProvider = ({ children }: PropsWithChildren) => {
    const { updateUrlParams, queryParams } = useQueryParams<ObjectCardQueryParamValues>();
    const { selectedScenario } = useSelectedScenario();
    const [newObjectConfig, setNewObjectConfig] = useState<NewObjectConfig>(
        getDefaultNewObjectConfig(),
    );
    const [isExternalObjectViewer, setIsExternalObjectViewer] = useState<boolean>();
    const [externalObject, setExternalObject] = useState<GetObjectCardObjectQuery['getModularObjectByIDs'][0]>();
    const lastScenarioRef = useRef(selectedScenario);

    // Didn't want to use useEffect but the fact that the templateId is set via a useState kind of forces our hand without refactoring
    useEffect(() => {
        if (queryParams?.templateId) {
            setNewObjectConfig((prev) => ({ ...prev, templateId: queryParams.templateId }));
        }
    }, [queryParams?.templateId]);

    // todo: get rid of this.  we need to be managing external links more succinctly
    const handleSetExternalObjectViewer = useCallback(
        (isExternalObjectViewer: boolean, object: GetObjectCardObjectQuery['getModularObjectByIDs'][0]) => {
            setIsExternalObjectViewer(isExternalObjectViewer);
            setExternalObject(object);
        },
        [],
    );

    // todo: get rid of this.  we need to be managing external links more succinctly
    const resetExternalObjectCrap = useCallback(() => {
        setIsExternalObjectViewer(false);
        setExternalObject(null);
    }, []);

    const isObjectCardDrawerOpen = Boolean(queryParams?.isObjectCardOpen);
    const isObjectCardFullWidth = Boolean(queryParams?.fullWidth);

    const openViewObjectCardDrawer = useCallback(
        ({ modularObjectId, viewCollabModal = 'false' }: OpenViewObjectCardDrawerArgs) => {
            // Clear any existing card state
            setNewObjectConfig(getDefaultNewObjectConfig());

            // Update URL params to open the card
            updateUrlParams({
                ...queryParams, // Preserve all other existing params
                modularObjectId,
                viewCollabModal: viewCollabModal as 'true' | 'false',
                isObjectCardOpen: 'true',
                fullWidth: 'true',
            });
        },
        [updateUrlParams],
    );

    const openCreateNewObjectCardDrawer = useCallback((newObjectConfig: NewObjectConfig) => {
        // Clear any existing card state
        setNewObjectConfig(newObjectConfig);
        // Update URL params to open the card
        updateUrlParams({
            isObjectCardOpen: 'true',
            tid: newObjectConfig.templateId,
            fullWidth: 'true',
        });
        resetExternalObjectCrap();
    }, [updateUrlParams, resetExternalObjectCrap]);

    const closeObjectCardDrawer = useCallback(
        () => {
            const { modularObjectId, tid, viewCollabModal, isObjectCardOpen, fullWidth, ...rest } = queryParams;
            setNewObjectConfig(getDefaultNewObjectConfig());
            updateUrlParams(rest);
        },
        [queryParams, updateUrlParams],
    );

    const closeCollaborationModal = useCallback(() => {
        const { viewCollabModal, ...rest } = queryParams;
        updateUrlParams(rest);
    }, [queryParams, updateUrlParams]);

    const openCollaborationModal = useCallback(() => {
        updateUrlParams({ ...queryParams, viewCollabModal: 'true' });
    }, [queryParams, updateUrlParams]);

    const activeModularObjectId = queryParams?.modularObjectId;
    const isCollabModalOpen = Boolean(queryParams?.viewCollabModal === 'true');

    // Close card when switching scenarios
    useEffect(() => {
        if (selectedScenario !== lastScenarioRef.current) {
            lastScenarioRef.current = selectedScenario;
            closeObjectCardDrawer();
        }
    }, [selectedScenario, closeObjectCardDrawer]);

    const {
        data,
        loading: isLoadingObjectCardData,
        previousData,
    } = useGetObjectCardObjectQuery({
        variables: { modularObjectId: activeModularObjectId },
        fetchPolicy: 'cache-first',
        skip: !activeModularObjectId,
        onCompleted: (data) => {
            if (!data.getModularObjectByIDs) {
                console.error('No object returned from fetching card data', activeModularObjectId);
                addToastError('Unable to access object.');
                closeObjectCardDrawer();
            }
        },
        onError: (error) => {
            console.error('Error fetching object card data', error);
            addToastError(`Unable to access object. ${error.message}`);
            closeObjectCardDrawer();
        },
    });

    const GET_STREAMING_OBJECTS = gql`
    query getStreamingObjects {
      streamingObjects @client
    }
  `;

    const { data: streamingData = {} } = useQuery(GET_STREAMING_OBJECTS) ?? {};

    const _data = isLoadingObjectCardData ? previousData : data;
    const objectCardData = useMemo(() => {
        if (Object.keys(streamingData?.streamingObjects ?? {}).length && !_data?.getModularObjectByIDs) {
            return streamingData?.streamingObjects[activeModularObjectId];
        }

        if (!_data?.getModularObjectByIDs?.length && !externalObject) {
            return safeDefault;
        }

        if (isExternalObjectViewer) {
            if (!externalObject) {
                return safeDefault;
            }
            return externalObject;
        }

        return _data.getModularObjectByIDs[0];
    }, [_data?.getModularObjectByIDs, externalObject, isExternalObjectViewer]);

    useEffect(() => {
        // Add an event listener to close the object card drawer when the user clicks outside of it
        const handleClickedOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            if (
                target.closest('[data-testid="schedule-gantt"]') &&
                !(target.closest('[id^="gantt-object-"]') ||
                    target.closest('[data-testid^="object-bar-content-"]') ||
                    target.closest('[data-testid="view-children-toggle"]'))
            ) {
                closeObjectCardDrawer();
            } else if (
                target.closest(`[id="gantt-object-${activeModularObjectId}"]`) ||
                target.closest(`[data-testid^="object-bar-content-${activeModularObjectId}"]`)
            ) {
                closeObjectCardDrawer();
                e.stopPropagation();
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeObjectCardDrawer();
            }
        };

        markModularObjectRead(activeModularObjectId);

        document.addEventListener('click', handleClickedOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('click', handleClickedOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeModularObjectId, closeObjectCardDrawer]);

    const isUnread = useUnreadModularObjects(activeModularObjectId);
    useEffect(() => {
        if (isUnread) {
            // Delay marking the object as read to allow the unread dot to be displayed while the card is open
            setTimeout(() => {
                markModularObjectRead(activeModularObjectId);
            }, 2000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUnread]);

    const api = useMemo(() => {
        return {
            isObjectCardDrawerOpen,
            isObjectCardFullWidth,
            isCollabModalOpen,
            closeObjectCardDrawer,
            closeCollaborationModal,
            openCollaborationModal,
            activeModularObjectId,
            openViewObjectCardDrawer,
            openCreateNewObjectCardDrawer,
            newObjectConfig,
            isLoadingObjectCardData,
            objectCardData,
            handleSetExternalObjectViewer,
        };
    }, [
        isObjectCardDrawerOpen,
        isObjectCardFullWidth,
        isCollabModalOpen,
        closeObjectCardDrawer,
        closeCollaborationModal,
        openCollaborationModal,
        activeModularObjectId,
        openViewObjectCardDrawer,
        openCreateNewObjectCardDrawer,
        newObjectConfig,
        isLoadingObjectCardData,
        objectCardData,
        handleSetExternalObjectViewer,
    ]);

    return (
        <ObjectCardContext.Provider value={api}>
            {children}
        </ObjectCardContext.Provider>
    );
};
